import { Button, Icon, Typography } from '@smartgift/frontend-toolkit';
import React, { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { Notification } from '@hero/atoms';
import { ThankYouReplySettingsModal } from '@hero/components/modals';
import { useValidationErrorsStore } from '@hero/components/providers/ValidationErrorsProvider';
import { configs } from '@hero/configs';
import {
  APPROVAL_REASON_GIFT_CARD,
  APPROVAL_REASON_PUNCHOUT,
  APPROVAL_STATUSES,
  CARDISLE_GREETING_CARD,
  CONTACT_TYPE,
  FLOW_CODES,
  MAX_ALLOWED_CONTACT_COUNT_FCC,
  PAYMENT_METHOD_TYPES,
  PRODUCT_TYPES,
  RECOGNITION_STATUSES,
  SEND_GIFT_ROUTE_PATHS
} from '@hero/constants';
import {
  useDebounceEffect,
  useDeleteModalTemplate,
  useMaxContactHasError,
  usePreviousValue,
  useSelectedOrganization
} from '@hero/hooks';
import { PageLayout } from '@hero/layouts';
import { CostSummary, OnboardingCard, StatusSummary, ToggleMenuGroup } from '@hero/molecules';
import {
  ContactBGSModal,
  GiftPreviewBox,
  OrderConfirmationModal,
  ReviewAndPayContactsAccordion,
  RecognitionExperienceAccordion,
  RecognitionGiftsAccordion,
  ReviewAndPayOverviewAccordion
} from '@hero/organisms';
import { ROUTE_PATHS } from '@hero/routes';
import { useGetMerchant } from '@hero/services/merchant-service/hooks';
import {
  useDeleteRecognition,
  useDuplicateRecognition,
  useGetRecognition,
  useGetRecognitionProducts,
  useGetRecognitionRecipientList,
  useGetRecognitionSpendingSummary,
  useStartRecognition,
  useSubmitToApproval,
  useUpdateRecognition
} from '@hero/services/order-service';
import { useGetUser } from '@hero/services/organization-service/hooks';
import { useNotification, useOnboardingStore } from '@hero/store/hooks';
import { useValidateRules } from '@hero/store/hooks/useValidateRules';
import { cleanObjectEmptyFields, getAddonData, hasAnyAvailableProducts } from '@hero/utils';
import { getReviewAndPaySchema } from '@hero/validations';

import HeaderNotificationContent from './HeaderNotificationContent';
import { NavigationCenterContent } from './NavigationCenterContent';
import { NavigationEndContent } from './NavigationEndContent';
import { NavigationSecondaryContent } from './NavigationSecondaryContent';
import * as S from './style';

const VIA = {
  email: 'email',
  link: 'link'
};

function RecognitionReviewAndPayTemplate() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { recognitionCode } = useParams();
  const { data: merchantData } = useGetMerchant({ options: { includeConfigs: true } });
  const { recognition: onboardingRecognition } = useOnboardingStore();
  const organization = useSelectedOrganization();
  const { showNotification } = useNotification();

  const { mutateAsync: duplicateRecognition } = useDuplicateRecognition();
  const { showDeleteModal } = useDeleteModalTemplate({
    title: t('areYouSureYouWantDelete', { type: t('oneTimeGift').toLowerCase() }),
    message: t('deleteRecognitionModalDesc'),
    confirmText: 'DELETE GIFT',
    maxLength: 18
  });

  const {
    data: recognition,
    refetch: refetchRecognition,
    isLoading: isLoadingRecognition
  } = useGetRecognition(
    {
      recognitionCode
    },
    { includeGoLiveChecks: true }
  );

  const {
    data: recognitionSpendingSummary,
    isFetching: isFetchingSpendingSummary,
    isLoading: isLoadingSpendingSummary,
    isError: isErrorSpendingSummary
  } = useGetRecognitionSpendingSummary({
    recognitionCode
  });
  const {
    mutateAsync: startRecognition,
    isSuccess: isSuccessStartRecognition,
    isLoading: isLoadingStartRecognition
  } = useStartRecognition();
  const { mutateAsync: submitToApproval } = useSubmitToApproval();
  const { data: userData, isLoading: isLoadingUserData } = useGetUser();
  const { isLoading: isUpdateRecognitionLoading, mutateAsync: updateRecognition } =
    useUpdateRecognition();

  const { mutateAsync: deleteRecognition } = useDeleteRecognition();

  const { data: currentProducts } = useGetRecognitionProducts(recognitionCode);
  const { data: recognitionRecipients } = useGetRecognitionRecipientList(
    {
      code: recognitionCode
    },
    {
      enabled: !!recognitionCode
    }
  );

  const contacts = usePreviousValue(recognition?.contacts);
  const maxContactHasError = useMaxContactHasError({
    recognition,
    recognitionCode,
    contacts: recognition?.contacts
  });

  const [bgsContactInfoBoxHide, setBgsContactInfoBoxHide] = useState(false);
  const [isShowOrderConfirmationModal, setShowOrderConfirmationModal] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [isOnboardingCardGift, setIsOnboardingCardGift] = useState(false);
  const [isOnboardingCardRecipient, setIsOnboardingCardRecipient] = useState(false);
  const [isOnboardingCardExperience, setIsOnboardingCardExperience] = useState(false);
  const [isOnboardingCardPreview, setIsOnboardingCardPreview] = useState(false);
  const [defaultActiveMenus, setDefaultActiveMenus] = useState(['overview']);
  const [contactBGSModal, setContactBGSModal] = useState(false);
  const [showThankYouModal, setShowThankYouModal] = useState(false);
  const [receiveThankYouNotes, setReceiveThankYouNotes] = useState(false);

  const TopContent = (
    <Typography variant="body1Bold" color="neutral" emphasis="500">
      {t('reviewBeforeSending')}:
    </Typography>
  );

  const hasEGiftCardItem = currentProducts?.some(
    p => p?.meta?.productType === PRODUCT_TYPES.GIFT_CARD
  );

  const isRecognitionLive =
    recognition?.status?.current !== RECOGNITION_STATUSES.draft &&
    recognition?.status?.current !== RECOGNITION_STATUSES.approved &&
    recognition?.status?.current !== RECOGNITION_STATUSES.pending_approval;

  const isRecognitionStarted =
    !isLoadingRecognition && isRecognitionLive && !isSuccessStartRecognition;

  const maxAllowedContactFCC = merchantData?.configs?.[MAX_ALLOWED_CONTACT_COUNT_FCC];

  const approvalType = recognition?.approval?.reason;
  const isApprovalRequired = recognition?.approval?.status === APPROVAL_STATUSES.required;
  const isApprovalPending = recognition?.status?.current === RECOGNITION_STATUSES.pending_approval;
  const isApprovalBoxShown =
    isApprovalRequired && recognition?.status?.current === RECOGNITION_STATUSES.draft;
  const isCancelApprovalBoxShown = recognition && isApprovalRequired && isApprovalPending;
  const isEGiftCardRequired =
    recognition?.approval?.reason === PRODUCT_TYPES.GIFT_CARD &&
    recognition?.approval?.status === APPROVAL_STATUSES.required;

  const isApprovalProcessing = isApprovalBoxShown || isCancelApprovalBoxShown;

  const isBGSContactInfoBoxShown =
    !bgsContactInfoBoxHide &&
    maxAllowedContactFCC < recognition?.numberOfContacts &&
    recognition?.paymentMethod?.type === PAYMENT_METHOD_TYPES.creditCard;

  const isContactsFlow = contacts?.type === CONTACT_TYPE.list;
  const isCCPayment = recognition?.paymentMethod?.type === PAYMENT_METHOD_TYPES.creditCard;

  const digitalGreetingCard =
    configs?.experience?.greetingCardEnabled &&
    getAddonData(recognition?.addons, CARDISLE_GREETING_CARD);

  const giftTitleLabel = (
    <>
      <Typography variant="body1Bold">{t('gifts')}</Typography>
      <OnboardingCard
        styles={{
          wrapper: {
            right: 12,
            top: -12
          }
        }}
        position="right-start"
        cardOffset={[22, -22]}
        isCardShown={isOnboardingCardGift}
        setIsCardShown={setIsOnboardingCardGift}
        title={t('typeNameDetails', { name: t('gift') })}
        description={t('giftDetailsOnboardingCardDesc', { feature: t('oneTimeGift') })}
        isInfoActive={onboardingRecognition?.infoCardsActive}
        featureType="recognition"
        onTourButtonClick={() => {
          setIsOnboardingCardRecipient(true);
          setDefaultActiveMenus(['recipients']);
        }}
      />
    </>
  );

  const recipientTitleLabel = (
    <>
      <Typography variant="body1Bold">{t('recipients')}</Typography>
      <OnboardingCard
        styles={{
          wrapper: {
            right: 12,
            top: -12
          }
        }}
        position="right-start"
        cardOffset={[22, -22]}
        isCardShown={isOnboardingCardRecipient}
        setIsCardShown={setIsOnboardingCardRecipient}
        title={t('typeNameDetails', { name: t('recipient') })}
        description={t('recipientDetailsOnboardingCardDesc', { feature: t('oneTimeGift') })}
        isInfoActive={onboardingRecognition?.infoCardsActive}
        featureType="recognition"
        onTourButtonClick={() => {
          setIsOnboardingCardExperience(true);
          setDefaultActiveMenus(['experience']);
        }}
      />
    </>
  );

  const experienceTitleLabel = (
    <>
      <Typography variant="body1Bold">{t('experience')}</Typography>
      <OnboardingCard
        styles={{
          wrapper: {
            right: 12,
            top: -12
          }
        }}
        position="right-start"
        cardOffset={[22, -22]}
        isCardShown={isOnboardingCardExperience}
        setIsCardShown={setIsOnboardingCardExperience}
        title={t('recipientExperience')}
        description={t('recipientExperienceOnboardingCardDesc')}
        isInfoActive={onboardingRecognition?.infoCardsActive}
        featureType="recognition"
        onTourButtonClick={() => {
          setIsOnboardingCardPreview(true);
        }}
      />
    </>
  );

  const cardList = [
    {
      id: 'overview',
      icon: <Icon name="order" color="var(--color-accent-primary-default)" />,
      title: <Typography variant="body1Bold">{t('overview')}</Typography>,
      hasWarningIcon:
        !!recognition && (!recognition?.settings?.expiresAt || !recognition?.paymentMethod),
      children: () => (
        <ReviewAndPayOverviewAccordion
          onClickOnboardingInfoCard={() => {
            setIsOnboardingCardGift(true);
            setDefaultActiveMenus(['gifts']);
          }}
          isApprovalProcessing={isApprovalProcessing}
        />
      )
    },
    {
      id: 'gifts',
      icon: <Icon name="gift-box" color="var(--color-accent-primary-default)" />,
      title: giftTitleLabel,
      children: () => <RecognitionGiftsAccordion refetchRecognition={refetchRecognition} />,
      TopContent
    },
    {
      id: 'recipients',
      icon: <Icon name="contacts" color="var(--color-accent-primary-default)" />,
      title: recipientTitleLabel,
      children: () => <ReviewAndPayContactsAccordion editableContactList />
    },
    {
      id: 'experience',
      icon: <Icon name="smartphone" color="var(--color-accent-primary-default)" />,
      title: experienceTitleLabel,
      children: () => <RecognitionExperienceAccordion hideRecipientExperienceButton />
    }
  ];

  const startRecognitionAndNavigateShare = async type => {
    setShowOrderConfirmationModal(false);
    setIsPageLoading(true);
    try {
      await startRecognition(recognitionCode);
      const hasContacts = contacts?.type === CONTACT_TYPE.list;

      if (type === VIA.email && hasContacts) {
        navigate(ROUTE_PATHS.NEW_RECOGNITION_ORDER_CONFIRMED(recognitionCode), { replace: true });
      } else {
        navigate(ROUTE_PATHS.NEW_RECOGNITION_SHARE(recognitionCode), { replace: true });
      }
    } finally {
      setIsPageLoading(false);
    }
  };

  const submitForApproval = async () => {
    if (approvalType === APPROVAL_REASON_PUNCHOUT) {
      const response = await submitToApproval({
        code: recognitionCode,
        approvalType: APPROVAL_REASON_PUNCHOUT
      });

      window.location.href = response.redirectLink;
      return;
    }

    if (approvalType === APPROVAL_REASON_GIFT_CARD) {
      await submitToApproval({
        code: recognitionCode,
        approvalType: APPROVAL_REASON_GIFT_CARD
      });

      showNotification({
        header: t('eGiftCardApprovalPending'),
        customContent: () => (
          <S.CustomApprovalNotificationContent>
            <Notification
              style={{ margin: '8px 0' }}
              status="info"
              message={
                <Trans i18nKey="eGiftCardWasCouldNotAddedRecognition" components={{ b: <b /> }} />
              }
            />
            <Icon name="clock" size="xxl" />
            <Typography>
              <Trans i18nKey="yourApprovalRequestIsReviewed" components={{ b: <b /> }} />
            </Typography>
          </S.CustomApprovalNotificationContent>
        )
      });
    }
  };

  const handleCancelApproval = () => {
    showNotification({
      title: t('cancelApprovalPromptQuestion'),
      notificationImage: <Icon name="error" size="xxl" color="var(--color-error-default)" />,
      primaryButtonProps: {
        children: t('yesCancel'),
        color: 'error',
        onClick: async () =>
          await updateRecognition(
            {
              params: { recognitionCode },
              body: {
                status: { current: RECOGNITION_STATUSES.draft }
              }
            },
            { successMessage: t('successfullySentApproveToFacilityMessage') }
          )
      },
      secondaryButtonProps: {
        children: t('noDoNotCancel')
      }
    });
  };

  const getPrimaryButton = ({ excludeThankYouNote = false } = {}) => {
    const button = {
      children: t('generateLink'),
      variant: 'contained',
      color: 'primary',
      onClick: async () => await startRecognitionAndNavigateShare()
    };

    if (
      isContactsFlow &&
      !recognition?.settings?.receiveThankYouNotes &&
      recognition?.numberOfContacts <= 10 &&
      !excludeThankYouNote &&
      !isApprovalRequired
    ) {
      if (isCCPayment) {
        button.children = t('payAndSend');
      } else {
        button.children = t('send');
      }
      button.onClick = () => setShowThankYouModal(true);
      return button;
    }

    if (isApprovalBoxShown) {
      button.children = t('submitForApproval');
      button.onClick = async () => await submitForApproval();
      return button;
    }

    if (isCancelApprovalBoxShown) {
      button.disabled = true;

      return button;
    }

    if (isCCPayment) {
      if (maxAllowedContactFCC < recognition?.numberOfContacts) {
        button.children = t('generateLink');
        button.onClick = () => setContactBGSModal(true);
        return button;
      }

      if (!isApprovalRequired) {
        if (isContactsFlow) {
          button.children = t('payAndSend');
          button.onClick = () => setShowOrderConfirmationModal(true);
          return button;
        } else {
          button.children = t('payAndGenerateLink');
          button.onClick = async () => await startRecognitionAndNavigateShare();
          return button;
        }
      }
    }

    if (isContactsFlow) {
      button.children = t('send');
      button.onClick = () => setShowOrderConfirmationModal(true);
      return button;
    }

    return button;
  };

  const { addError } = useValidationErrorsStore();
  const { validate } = useValidateRules();

  const checkCustomErrors = async () => {
    let hasCustomError = false;
    if (contacts?.type === CONTACT_TYPE.list && !recognitionRecipients?.totalDocs) {
      await addError(<Trans key="getContactsTypeRule_order=20" i18nKey="errors.noRecipient" />);
      hasCustomError = true;
    }
    if (maxContactHasError) {
      await addError(<Trans key="maxRecipientError_order=21" i18nKey="errors.maxRecipientError" />);
      hasCustomError = true;
    }
    if (!!currentProducts && !hasAnyAvailableProducts(currentProducts)) {
      await addError(
        <Trans
          key="noAvailableProducts"
          i18nKey="noAvailableProducts"
          values={{ feature: t('oneTimeGift') }}
        />
      );
      hasCustomError = true;
    }

    return hasCustomError;
  };

  const checkPageIsValid = async () => {
    const schemaFunc = await getReviewAndPaySchema();
    const schemaRule = schemaFunc({
      isPaymentMethodRequired: configs?.paymentMethods?.enabled,
      isEndDateRequired: !isApprovalProcessing,
      isInputMessageRequired: !digitalGreetingCard,
      isPoNumberRequired: !!organization?.configs?.poNumberRequired,
      isCostCenterRequired: !!organization?.configs?.costCenterRequired
    });

    const isValid = await validate([
      {
        rule: schemaRule,
        value: {
          ...recognition,
          experienceSettings: {
            ...recognition.experienceSettings,
            messageParams: {
              ...recognition.experienceSettings.messageParams,
              digitalGreetingCard: !!digitalGreetingCard
            }
          }
        }
      }
    ]);

    const hasCustomError = await checkCustomErrors();
    return isValid && !hasCustomError;
  };

  const isLoadingPayAndSend = useMemo(() => {
    return (
      isLoadingStartRecognition ||
      isUpdateRecognitionLoading ||
      isPageLoading ||
      isLoadingUserData ||
      isLoadingSpendingSummary
    );
  }, [
    isLoadingStartRecognition,
    isUpdateRecognitionLoading,
    isPageLoading,
    isLoadingUserData,
    isLoadingSpendingSummary
  ]);

  const payAndSendButtonProps = useMemo(
    () => ({
      icon: false,
      loading: isLoadingPayAndSend,
      style: {
        width: '100%'
      },
      ...getPrimaryButton(),
      onClick: async () => {
        const isPageValid = await checkPageIsValid();
        if (!isPageValid && !isApprovalProcessing) return;
        await getPrimaryButton().onClick();
      }
    }),
    [
      isLoadingPayAndSend,
      recognition,
      isLoadingStartRecognition,
      isApprovalProcessing,
      isContactsFlow,
      hasEGiftCardItem
    ]
  );

  useDebounceEffect(
    () => {
      if (isRecognitionStarted) {
        navigate(ROUTE_PATHS.MY_RECOGNITION_DETAIL(recognitionCode));
      }
    },
    500,
    [recognition]
  );

  return (
    <PageLayout
      isLoading={isPageLoading || isLoadingPayAndSend}
      header={{
        title: t('reviewAndPay'),
        showBorder: true,
        components: {
          CenterContent: NavigationCenterContent,
          EndContent: NavigationEndContent
        },
        style: {
          backgroundColor: 'var(--color-bg-weakest2)'
        }
      }}
      navigation={{
        rightButton: payAndSendButtonProps,
        leftButton: {
          children: t('goToOneTimeGifts'),
          onClick: () => navigate(ROUTE_PATHS.MY_RECOGNITIONS)
        },
        components: {
          SecondaryContent: NavigationSecondaryContent
        }
      }}
    >
      <HeaderNotificationContent
        iseGiftCardApprovalBoxShown={isEGiftCardRequired && hasEGiftCardItem}
        recognitionCode={recognitionCode}
      />
      <S.HeadTitle variant="subtitle4Bold">{t('reviewAndPayHeaderSubTitle')}</S.HeadTitle>
      <S.Wrapper>
        {isBGSContactInfoBoxShown && (
          <Notification
            status="info"
            isClosable
            onClose={() => setBgsContactInfoBoxHide(true)}
            title={t('okIfWeGiveYouCall')}
            style={{ marginBottom: 16 }}
            message={
              <Trans
                i18nKey="clickHereTailoredSolutionDesc"
                components={{
                  b: <b />,
                  button: (
                    <Button
                      style={{ display: 'inline-block', fontSize: 14 }}
                      color="neutral"
                      variant="link"
                      onClick={() => {
                        setContactBGSModal(true);
                      }}
                    />
                  )
                }}
              />
            }
          />
        )}
        <S.PageContent>
          <ToggleMenuGroup
            multiMenuActive
            list={cardList}
            defaultActiveMenus={defaultActiveMenus}
          />
          <S.RightContent>
            <CostSummary
              {...(recognitionSpendingSummary || {})}
              payAndSendButtonProps={payAndSendButtonProps}
              featureType="recognition"
              feature={recognition}
              status={recognition?.status?.current}
              isFetching={isFetchingSpendingSummary}
              isError={isErrorSpendingSummary}
              isCCPayment={isCCPayment}
              isLoadingFeature={isLoadingRecognition}
              isLoadingSpendingSummary={isLoadingSpendingSummary || isFetchingSpendingSummary}
              contactType={recognition?.contacts?.type}
              onDuplicate={async formValues => {
                const response = await duplicateRecognition({
                  code: recognition.code,
                  body: formValues
                });

                if (
                  recognition.flowCode === FLOW_CODES.simplified ||
                  recognition.flowCode === FLOW_CODES.ai
                ) {
                  navigate(
                    ROUTE_PATHS[SEND_GIFT_ROUTE_PATHS[recognition.meta.creationStep]](response.code)
                  );
                }

                navigate(ROUTE_PATHS.NEW_RECOGNITION_REVIEW_PAY(response.code));
              }}
              onDelete={() => {
                showDeleteModal({
                  onSubmit: async () => {
                    await deleteRecognition(recognitionCode);
                    navigate(ROUTE_PATHS.MY_RECOGNITIONS);
                  }
                });
              }}
            />
            {isRecognitionLive && (
              <StatusSummary
                duplicateText={recognition?.name}
                featureType="recognition"
                feature={recognition}
                spendingSummary={recognitionSpendingSummary}
                isCCPayment={isCCPayment}
                isContactLess={recognition?.contacts?.type === CONTACT_TYPE.number}
                onCancelApproval={handleCancelApproval}
              />
            )}
            <GiftPreviewBox
              giftWrapperUrl={recognition?.experienceSettings?.branding?.giftWrapperUrl}
              previewLink={recognition?.link?.preview}
              digitalGreetingCard={digitalGreetingCard}
            />
          </S.RightContent>
        </S.PageContent>

        {!isPageLoading && recognition?.experienceSettings && (
          <OrderConfirmationModal
            experienceSettings={recognition?.experienceSettings}
            isModalOpen={isShowOrderConfirmationModal}
            onModalOpen={() => setShowOrderConfirmationModal(false)}
            onCreateGiftLink={startRecognitionAndNavigateShare}
            onSendViaEmail={async input => {
              setIsPageLoading(true);

              await updateRecognition({
                params: { recognitionCode },
                body: {
                  experienceSettings: {
                    ...recognition?.experienceSettings,
                    sharingMethod: cleanObjectEmptyFields({
                      type: 'email',
                      params: {
                        subject: input
                      }
                    })
                  }
                }
              });

              await startRecognitionAndNavigateShare(VIA.email);
            }}
          />
        )}

        <OnboardingCard
          styles={{
            wrapper: {
              left: 200,
              top: 20
            }
          }}
          position="bottom-end"
          cardOffset={[22, -10]}
          isCardShown={isOnboardingCardPreview}
          setIsCardShown={setIsOnboardingCardPreview}
          title={t('giftPreview')}
          description={t('giftPreviewOnboardingCardDesc')}
          isInfoActive={onboardingRecognition?.infoCardsActive}
          featureType="recognition"
        />

        <ContactBGSModal
          recognitionCode={recognitionCode}
          isModalOpen={contactBGSModal}
          onModalOpen={() => setContactBGSModal(false)}
        />
        <ThankYouReplySettingsModal
          onRequestClose={() => {
            setShowThankYouModal(false);
          }}
          onPrimaryButtonClick={async () => {
            await updateRecognition({
              params: { recognitionCode },
              body: {
                settings: {
                  receiveThankYouNotes
                }
              }
            });
            setShowThankYouModal(false);
            getPrimaryButton({
              excludeThankYouNote: true
            }).onClick();
          }}
          isOpen={showThankYouModal}
          receiveThankYouNotes={receiveThankYouNotes}
          setReceiveThankYouNotes={receiveThankYouNotes => {
            setReceiveThankYouNotes(receiveThankYouNotes);
          }}
          senderName={userData?.firstName || t('sender')}
          isLoading={isUpdateRecognitionLoading}
        />
      </S.Wrapper>
    </PageLayout>
  );
}

export { RecognitionReviewAndPayTemplate };
